import React, { CSSProperties, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import {
  color,
  display,
  DisplayProps,
  flex,
  FlexProps,
  fontSize,
  FontSizeProps,
  fontStyle,
  FontStyleProps,
  fontWeight,
  FontWeightProps,
  letterSpacing,
  LetterSpacingProps,
  lineHeight,
  LineHeightProps,
  maxHeight,
  MaxHeightProps,
  maxWidth,
  MaxWidthProps,
  minHeight,
  MinHeightProps,
  minWidth,
  MinWidthProps,
  opacity,
  OpacityProps,
  space,
  SpaceProps,
  textAlign,
  TextAlignProps,
  themeGet,
  width,
  WidthProps
} from 'styled-system';
import { Classes } from '@blueprintjs/core';
import classNames from 'classnames';

import { DisplayAs } from 'core/components/types';
import { fontVariant, textDecoration, textOverflow, textTransform, whiteSpace } from './utils/typography';

type ExtendedFontWeightProps = { fontWeight?: FontWeightProps['fontWeight'] | 'medium' };

interface StyledTextProps
  extends
    SpaceProps,
    WidthProps,
    FlexProps,
    ExtendedFontWeightProps,
    FontSizeProps,
    DisplayProps,
    LineHeightProps,
    OpacityProps,
    FontStyleProps,
    TextAlignProps,
    LetterSpacingProps,
    MinHeightProps,
    MaxHeightProps,
    MinWidthProps,
    MaxWidthProps,
    Pick<CSSProperties, 'fontVariant' | 'textTransform' | 'whiteSpace'> {
  color?: string;
  hoverColor?: string;
  textDecoration?: string;
  style?: CSSProperties;
}

const StyledText = styled.span<StyledTextProps>`
  ${space};
  ${width};
  ${flex};
  ${fontSize};
  ${display};
  ${fontWeight};
  ${color};
  ${textTransform};
  ${textDecoration};
  ${textAlign};
  ${fontStyle};
  ${fontVariant};
  ${letterSpacing};
  ${lineHeight};
  ${opacity};
  ${whiteSpace};
  ${textOverflow};
  ${minHeight};
  ${maxHeight};
  ${minWidth}; // Dont think this works in inline elements
  ${maxWidth};

  ${({ hoverColor }) =>
    hoverColor &&
    css`
      &:hover {
        color: ${themeGet(`colors.${hoverColor}`, hoverColor)};
      }
    `}
`;

export interface TextProps extends React.HTMLAttributes<HTMLElement>, StyledTextProps {
  className?: string;
  as?: DisplayAs;
  monospace?: boolean;
  runningText?: boolean;
  large?: boolean;
  small?: boolean;
  showSkeleton?: boolean;
  muted?: boolean;
  disabled?: boolean;
  ellipsis?: boolean;
  children: React.ReactNode;
}

const Text = forwardRef<HTMLElement, TextProps>(
  (
    { as = 'span', className, monospace, runningText, large, small, showSkeleton, muted, disabled, ellipsis, ...rest },
    ref
  ): JSX.Element => {
    let { fontSize: fontSizeProp } = rest;
    if (monospace && small) {
      fontSizeProp = 10;
    }

    return (
      <StyledText
        ref={ref}
        as={as}
        {...rest}
        fontSize={fontSizeProp}
        className={classNames(
          {
            [Classes.MONOSPACE_TEXT]: monospace,
            [Classes.RUNNING_TEXT]: runningText,
            [Classes.TEXT_LARGE]: large,
            [Classes.TEXT_SMALL]: small,
            [Classes.TEXT_MUTED]: muted,
            [Classes.TEXT_DISABLED]: disabled,
            [Classes.TEXT_OVERFLOW_ELLIPSIS]: ellipsis,
            [Classes.SKELETON]: showSkeleton
          },
          className
        )}
      />
    );
  }
);

Text.displayName = 'Text';

export default Text;
